import { LegacyPortalDashboardService } from "@spwn-portal/grpc";
import firebase from "firebase/app";
import useSWR from "swr";

import { useConnectClient } from "../../utility/connectWeb";
import { minBigInt } from "./minBigInt";

import type { ConnectError } from "@bufbuild/connect";

export type TicketSalesByOrderDate = {
  orderDate: bigint;
  ticketName: string;
  quantity: number;
};

export type GetTicketSalesByOrderDateResponse = {
  ticketSalesByOrderDate: TicketSalesByOrderDate[];
  bigqueryLastWrittenAt?: bigint;
};

type GetTicketSalesByOrderDateError =
  | ConnectError
  | Error
  | { message: string };

const GetTicketSalesByOrderDateKey = "getTicketSalesByOrderDate";

export const useGetTicketSalesByOrderDate = (
  eventIds: string[],
  fromDate?: bigint,
  toDate?: bigint
) => {
  const auth = firebase.auth();
  const connectClient = useConnectClient(LegacyPortalDashboardService);
  const currentUserId = auth.currentUser?.uid ?? null;

  const {
    data: fetchedData,
    isLoading,
    isValidating,
    error,
    mutate,
  } = useSWR<GetTicketSalesByOrderDateResponse, GetTicketSalesByOrderDateError>(
    currentUserId && eventIds.length > 0
      ? [
          GetTicketSalesByOrderDateKey,
          currentUserId,
          [...eventIds].sort().join(","),
          fromDate?.toString() ?? "",
          toDate?.toString() ?? "",
        ]
      : null,
    async () => {
      if (!connectClient) {
        throw new Error("Connect client is not initialized");
      }
      try {
        const responses = await Promise.all(
          eventIds.map(async (eventId) =>
            connectClient.getTicketSalesByOrderDate({
              eventId,
              fromDate,
              toDate,
            })
          )
        );
        const ticketSalesByOrderDate = responses.flatMap((response) =>
          response.ticketSalesByOrderDate.map((item) => ({
            orderDate: item.orderDate,
            ticketName: item.ticketName,
            quantity: item.quantity,
          }))
        );
        const bigqueryLastWrittenAt = minBigInt(
          responses.map((response) => response.bigqueryLastWrittenAt)
        );
        return {
          ticketSalesByOrderDate,
          ...(bigqueryLastWrittenAt !== undefined
            ? { bigqueryLastWrittenAt }
            : {}),
        };
      } catch (error) {
        console.error("Error fetching ticket sales by order date:", error);
        throw error;
      }
    }
  );
  return {
    eventIds,
    isLoading,
    isValidating,
    error,
    mutate,
    fetchedData,
  };
};
